import { useCallback, useState } from 'react';
import html2pdf from 'html2pdf.js';
import logo from '../assets/logo.png';
import '../styles/report.css';

/**
 * Shared PDF export for both report pages.
 *
 * Before this, ProgressReport and ReportDetail each carried ~85 lines that
 * walked the DOM setting `display`, `pageBreakInside` and `pageBreakBefore`
 * inline, ran the export, then walked it again to put everything back. Two
 * copies, drifting apart, and any early return or thrown error left the report
 * visually broken on screen.
 *
 * Now: add one class, export, remove the class — and `styles/report.css` owns
 * what that class means. The restore is a single line in a `finally`, so a
 * failed export can't leave the page mangled.
 */

const CLINIC_FOOTER = 'Note: This is an electronically generated summary of the attached report.';

export const useReportExport = ({ elementRef, filename, clinicName, clinicContact }) => {
  const [exporting, setExporting] = useState(false);

  const download = useCallback(async () => {
    const element = elementRef.current;
    if (!element) return;

    setExporting(true);
    element.classList.add('pdf-mode');

    try {
      await html2pdf()
        .set({
          margin: [0.75, 0.5, 0.6, 0.5],
          filename: `${filename || 'report'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, logging: false, useCORS: true },
          jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] },
        })
        .from(element)
        .toPdf()
        .get('pdf')
        .then((pdf) => {
          const total = pdf.internal.getNumberOfPages();
          const width = pdf.internal.pageSize.getWidth();
          const height = pdf.internal.pageSize.getHeight();

          /**
           * Rules are drawn as filled rectangles, not stroked lines.
           *
           * The document is in inches and jsPDF's default lineWidth is 0.2 user
           * units — 0.2in, about 5mm — so an unset stroke renders as a thick grey
           * band that swallows the header date and the footer text. setLineWidth()
           * alone did not hold: the graphics state is reset between html2pdf's
           * toPdf() and this callback. A rectangle's height is literal geometry,
           * so it cannot be inflated by any state jsPDF carries.
           */
          const RULE = 0.007;          // ~0.5pt
          const rule = (y) => {
            pdf.setFillColor(228, 231, 236);
            pdf.rect(0.5, y, width - 1, RULE, 'F');
          };

          const HEADER_H = 0.56;       // white band the page content must clear
          const FOOTER_TEXT_Y = height - 0.3;
          const FOOTER_BAND_TOP = FOOTER_TEXT_Y - 0.24;

          for (let i = 1; i <= total; i += 1) {
            pdf.setPage(i);

            // --- Header ---------------------------------------------------
            // Opaque band first: it masks any page content that would otherwise
            // run underneath the logo and date.
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, width, HEADER_H, 'F');

            pdf.addImage(logo, 'PNG', 0.5, 0.13, 1.1, 0.28);

            pdf.setFontSize(8);
            pdf.setTextColor(102, 112, 133);
            if (clinicName) pdf.text(clinicName, width - 0.5, 0.22, { align: 'right' });
            if (clinicContact) pdf.text(clinicContact, width - 0.5, 0.33, { align: 'right' });
            // Sits clear of the rule below it rather than on top of it.
            pdf.text(`Generated ${new Date().toLocaleDateString()}`, width - 0.5, 0.44, { align: 'right' });

            rule(HEADER_H - RULE);

            // --- Footer ---------------------------------------------------
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, FOOTER_BAND_TOP, width, height - FOOTER_BAND_TOP, 'F');

            rule(FOOTER_BAND_TOP);

            pdf.setFontSize(7.5);
            pdf.setTextColor(102, 112, 133);
            pdf.text(CLINIC_FOOTER, 0.5, FOOTER_TEXT_Y);
            pdf.text(`Page ${i} of ${total}`, width - 0.5, FOOTER_TEXT_Y, { align: 'right' });
          }
        })
        .save();
    } finally {
      // Always restore, even if the export threw — the old code only restored
      // on the success path.
      element.classList.remove('pdf-mode');
      setExporting(false);
    }
  }, [elementRef, filename, clinicName, clinicContact]);

  return { download, exporting };
};

export default useReportExport;
